import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitFeedbackForm } from '../../actions/feedback';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Grid,
    Container,
    Alert,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Divider
} from '@mui/material';
import {
    Send as SendIcon,
    BugReport as BugIcon,
    Feedback as FeedbackIcon,
    Support as SupportIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    border: '1px solid rgba(255,255,255,0.2)',
    backdropFilter: 'blur(10px)',
}));



const Feedback = () => {
    const dispatch = useDispatch();
    const { loading, error, submitSuccess } = useSelector(state => state.feedback);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        category: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        if (!formData.name || !formData.email || !formData.message || !formData.category) {
            setSnackbar({
                open: true,
                message: 'Please fill in all required fields',
                severity: 'error'
            });
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setSnackbar({
                open: true,
                message: 'Please enter a valid email address',
                severity: 'error'
            });
            return;
        }

        try {
            // Dispatch the feedback form submission
            await dispatch(submitFeedbackForm(formData));
            
            // Reset form
            setFormData({
                name: '',
                email: '',
                subject: '',
                message: '',
                category: ''
            });
        } catch (error) {
            // Error is handled by the reducer
        }
    };

    // Handle Redux state changes
    useEffect(() => {
        if (submitSuccess) {
            setSnackbar({
                open: true,
                message: 'Thank you for your message! We\'ll get back to you soon.',
                severity: 'success'
            });
        }
    }, [submitSuccess]);

    useEffect(() => {
        if (error) {
            setSnackbar({
                open: true,
                message: error,
                severity: 'error'
            });
        }
    }, [error]);

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };



    const categories = [
        { value: 'bug', label: 'Bug Report', icon: <BugIcon /> },
        { value: 'feedback', label: 'Feedback', icon: <FeedbackIcon /> },
        { value: 'support', label: 'Technical Support', icon: <SupportIcon /> },
        { value: 'general', label: 'General Inquiry', icon: <InfoIcon /> }
    ];

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography 
                    variant="h5" 
                    component="h1" 
                    gutterBottom
                    color='primary.main'
                >
                    Contact Us
                </Typography>
                <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                        maxWidth: 600,
                        lineHeight: 1.6
                    }}
                >
                    Have a question, feedback, or need help? We're here to support you on your journey.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Contact Form */}
                <Grid item xs={12} md={8}>
                    <StyledPaper sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: '1px solid #e0e0e0'
                    }}>
                        <Box sx={{ 
                            p: 3,
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: '#f8f9fa'
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 600,
                                color: 'primary.main'
                            }}>
                                Send us a Message
                            </Typography>
                        </Box>
                        <Box sx={{ p: 3 }}>
                            <Box component="form" onSubmit={handleSubmit} noValidate>
                            <Grid container spacing={4}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Full Name *"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email Address *"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        variant="outlined"
                                        placeholder="Brief description of your inquiry"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Category *</InputLabel>
                                        <Select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleChange}
                                            label="Category *"
                                        >
                                            {categories.map((category) => (
                                                <MenuItem key={category.value} value={category.value}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {category.icon}
                                                        {category.label}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Message *"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        multiline
                                        rows={6}
                                        variant="outlined"
                                        placeholder="Tell us more about your inquiry..."
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="medium"
                                        startIcon={loading ? null : <SendIcon />}
                                        disabled={loading}
                                        sx={{
                                            backgroundColor: '#667eea',
                                            '&:hover': {
                                                backgroundColor: '#5a6fd8',
                                            }
                                        }}
                                    >
                                        {loading ? 'Sending...' : 'Send Message'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                        </Box>
                    </StyledPaper>
                </Grid>

                {/* Additional Information */}
                <Grid item xs={12} md={4}>
                    <StyledPaper sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: '1px solid #e0e0e0'
                    }}>
                        <Box sx={{ 
                            p: 3,
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: '#f8f9fa'
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 600,
                                color: 'primary.main'
                            }}>
                                What we can help with
                            </Typography>
                        </Box>
                        <Box sx={{ p: 3 }}>
                            <Box sx={{ mb: 3 }}>
                                {categories.map((category) => (
                                    <Box key={category.value} sx={{ mb: 2 }}>
                                        <Chip
                                            icon={category.icon}
                                            label={category.label}
                                            size="small"
                                            sx={{
                                                backgroundColor: '#667eea',
                                                color: 'white',
                                                '& .MuiChip-icon': {
                                                    color: 'white'
                                                }
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>

                            <Divider sx={{ my: 2 }} />
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                <strong>Response Time:</strong> We typically respond within 24 hours during business days.
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary">
                                <strong>Privacy:</strong> Your information is secure and will only be used to respond to your inquiry.
                            </Typography>
                        </Box>
                    </StyledPaper>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Feedback; 